import { useEffect, useMemo, useState } from "react";
import { BadgeCheck, ChevronDown, ClipboardList, Clock3, FileText, Fingerprint, History, Loader2, MessageCircle, MonitorSmartphone, RefreshCw, ShieldAlert, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";

const DIRECTION_LABELS = {
  b2b: "B2B-запрос",
  gold: "Золото",
  petroleum: "Нефтепродукты",
  both: "Совместный запрос",
  other: "Иной вопрос",
  callback: "WhatsApp-связь",
};

const STATE_META = {
  form_saved: { label: "Форма сохранена, код не запрашивался", cls: "border-sky-300/30 bg-sky-400/10 text-sky-200" },
  awaiting_code: { label: "Ожидает ввода кода", cls: "border-amber-300/30 bg-amber-400/10 text-amber-200" },
  code_entered_not_confirmed: { label: "Код введён, не подтверждён", cls: "border-orange-300/30 bg-orange-400/10 text-orange-200" },
  expired: { label: "Срок кода истёк", cls: "border-slate-300/20 bg-slate-400/10 text-slate-300" },
  failed: { label: "Подтверждение не завершено", cls: "border-rose-300/30 bg-rose-400/10 text-rose-200" },
  locked: { label: "Попытки исчерпаны", cls: "border-rose-300/30 bg-rose-400/10 text-rose-200" },
};

const EVENT_LABELS = {
  form_autosaved: "Форма автоматически сохранена",
  form_updated: "Данные формы обновлены",
  whatsapp_code_requested: "Запрошен код в WhatsApp",
  form_completed: "Форма заполнена",
  whatsapp_code_sent: "Код отправлен в WhatsApp",
  sms_fallback_sent: "Отправлен резервный SMS",
  sms_fallback_unavailable: "Резервный SMS недоступен",
  verification_code_entered: "Код введён",
  verification_not_confirmed: "Код не подтверждён",
  verification_check_unavailable: "Проверка кода временно недоступна",
  verification_locked: "Попытки кода исчерпаны",
  verification_expired: "Срок кода истёк",
  verification_failed: "Подтверждение не завершено",
  whatsapp_confirmed: "WhatsApp подтверждён",
  provider_delivery_delivered: "Доставка кода подтверждена сервисом",
  provider_delivery_failed: "Сервис сообщил о недоставке",
  provider_otp_approved: "Сервис подтвердил номер",
  provider_otp_failed: "Сервис отклонил подтверждение",
  number_confirmed: "Номер подтверждён",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "medium", timeZone: "Asia/Dubai" }).format(date);
};

const timelineDetails = (event) => {
  const parts = [];
  if (event?.details?.channel) parts.push(event.details.channel === "sms" ? "SMS" : "WhatsApp");
  if (event?.details?.attempt) parts.push(`попытка ${event.details.attempt}`);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
};

const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex min-w-0 gap-2.5">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-0.5 break-words text-sm text-slate-200">{children || "—"}</div>
    </div>
  </div>
);

export const UnconfirmedLeadRegistry = ({ api }) => {
  const [registry, setRegistry] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const response = await api.get("/leads/registry/unconfirmed");
      setRegistry({ count: Number(response.data?.count || 0), entries: Array.isArray(response.data?.entries) ? response.data.entries : [] });
    } catch {
      toast.error("Не удалось загрузить реестр неподтверждённых форм");
    } finally {
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleEntries = useMemo(() => (registry?.entries || []).slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [registry]);

  if (!registry) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>;
  }

  return (
    <div data-testid="admin-unconfirmed-lead-registry" className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-2xl text-slate-50 sm:text-3xl">Реестр <span className="text-[#D4AF37]">({registry.count})</span></h1>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-amber-400/10 px-3 py-1 text-xs font-medium text-amber-200"><ShieldAlert className="h-3.5 w-3.5" />Не подтверждены</span>
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-500">Заполненные формы с согласием на обработку данных. Здесь видны как сохранённые формы до запроса кода, так и обращения с незавершённым подтверждением номера. Они не попадают в обычную очередь заявок и не считаются подтверждёнными контактами.</p>
        </div>
        <button type="button" onClick={() => void load(true)} disabled={refreshing} data-testid="unconfirmed-registry-refresh" className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-white/25 hover:text-slate-100 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />Обновить</button>
      </section>

      <section className="rounded-[1.5rem] border border-[#D4AF37]/20 bg-[#D4AF37]/[0.045] p-4 sm:p-5">
        <div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" /><div><h2 className="text-sm font-semibold text-[#F3E5AB]">Внутренний служебный реестр</h2><p className="mt-1 text-xs leading-relaxed text-slate-400">Сохраняется IP-адрес, устройство, браузер, исходная страница, время, текст формы, согласие, канал отправки и этапы подтверждения. Одноразовый код, ключи API, webhook-подписи и технические идентификаторы OTP здесь не хранятся и не отображаются.</p></div></div>
      </section>

      {visibleEntries.length === 0 ? (
        <section data-testid="unconfirmed-registry-empty" className="flex flex-col items-center gap-4 rounded-[2rem] border border-white/8 bg-[#12141C] p-12 text-center sm:p-16"><ClipboardList className="h-10 w-10 text-slate-600" /><div><p className="text-lg font-medium text-slate-200">Сохранённых незавершённых форм нет</p><p className="mt-1 text-sm text-slate-500">Запись появится здесь после заполнения обязательных полей и согласия — ещё до запроса WhatsApp-кода.</p></div></section>
      ) : (
        <div className="space-y-4">
          {visibleEntries.map((entry) => {
            const status = STATE_META[entry.verification_state] || STATE_META.failed;
            const history = Array.isArray(entry.history) ? entry.history : [];
            return <article key={entry.id} data-testid={`unconfirmed-registry-card-${entry.id}`} className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[2rem] sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h2 className="break-words text-lg font-semibold text-slate-100">{entry.name || "Не указано имя"}</h2><span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${status.cls}`}><ShieldAlert className="h-3.5 w-3.5" />{status.label}</span></div>{entry.company && <p className="mt-1 text-sm text-slate-500">{entry.company}</p>}</div>
                <div className="text-left sm:text-right"><div className="text-xs text-slate-500">Форма заполнена</div><div className="mt-1 text-sm text-slate-300">{formatDate(entry.created_at)}</div></div>
              </div>

              <div className="mt-5 grid gap-4 border-y border-white/6 py-5 sm:grid-cols-2 xl:grid-cols-3">
                <Detail icon={MessageCircle} label="Телефон"><span className="font-medium text-slate-100">{entry.messenger}</span><span className="ml-2 text-xs text-amber-200">не подтверждён</span></Detail>
                <Detail icon={FileText} label="Email">{entry.email}</Detail>
                <Detail icon={ClipboardList} label="Направление">{DIRECTION_LABELS[entry.direction] || entry.direction || "—"}</Detail>
                <Detail icon={Fingerprint} label="IP-адрес">{entry.ip_address}</Detail>
                <Detail icon={MonitorSmartphone} label="Устройство">{entry.device}</Detail>
                <Detail icon={Smartphone} label="Канал">{entry.channel === "sms" ? "Резервный SMS" : entry.channel === "not_requested" ? "Код не запрашивался" : "WhatsApp"}</Detail>
                <Detail icon={Clock3} label="Попытки кода">{entry.record_type === "draft" ? "Код не вводился" : `${entry.attempt_count || 0} из ${entry.max_attempts || 3}`}</Detail>
                <Detail icon={BadgeCheck} label="Согласие">{entry.consent ? `Получено · ${formatDate(entry.consented_at)}` : "Не получено"}</Detail>
                <Detail icon={History} label="Исходная страница"><span className="break-all text-xs">{entry.source_url || "—"}</span></Detail>
              </div>

              {(entry.volume || entry.message) && <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,0.32fr)_minmax(0,0.68fr)]">{entry.volume && <div className="rounded-xl border border-white/8 bg-black/10 p-4"><div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Объём / запрос</div><p className="mt-2 text-sm text-slate-200">{entry.volume}</p></div>}{entry.message && <div className="rounded-xl border border-white/8 bg-black/10 p-4"><div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Сообщение посетителя</div><p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-300">{entry.message}</p></div>}</div>}

              <details className="group mt-5 rounded-xl border border-white/8 bg-black/10 p-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-200"><span className="inline-flex items-center gap-2"><History className="h-4 w-4 text-[#D4AF37]" />История подтверждения</span><ChevronDown className="h-4 w-4 text-slate-500 transition-transform group-open:rotate-180" /></summary>
                <div className="mt-4 space-y-3 border-l border-white/10 pl-4">
                  {history.length === 0 ? <p className="text-sm text-slate-500">Этапы ещё не зафиксированы.</p> : history.map((event, index) => <div key={`${event.event_type}-${event.occurred_at}-${index}`} className="relative"><span className="absolute -left-[1.31rem] top-1.5 h-2 w-2 rounded-full border border-[#D4AF37]/50 bg-[#12141C]" /><div className="text-sm text-slate-200">{EVENT_LABELS[event.event_type] || "Служебное событие"}{timelineDetails(event)}</div><div className="mt-0.5 text-xs text-slate-500">{formatDate(event.occurred_at)}</div></div>)}
                </div>
              </details>

              <details className="group mt-3 rounded-xl border border-white/6 px-4 py-3">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs text-slate-500"><span>Техническая информация браузера</span><ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" /></summary>
                <p className="mt-3 break-all rounded-lg bg-black/10 p-3 font-mono text-[11px] leading-relaxed text-slate-400">{entry.user_agent || "Не передан"}</p>
              </details>
            </article>;
          })}
        </div>
      )}
    </div>
  );
};
